import { Dispatch, SetStateAction, useEffect, useState } from "react";

import debounce from "lodash/debounce";

import {
    CreateUIAnalyticsEvent,
    useAnalyticsEvents
} from "@atlaskit/analytics-next";

import { ufoEventSiteNameAvailability } from "@/components/signup/utils";
import {
    IBxpWebStargateClient,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";

export enum ErrorType {
    MAXIMUM_LENGTH = "maximumLength",
    INVALID_NAME = "invalidName",
    UNAVAILABLE = "unavailable",
    NETWORK_ERROR = "networkError",
    NO_ERROR = "noError"
}

export type SiteNameValidation = {
    readonly isValidating: boolean;
    readonly isValid?: boolean;
    readonly error: ErrorType;
};

export type SiteNameValidationState = {
    readonly isValid?: boolean;
    readonly error: ErrorType;
};
export type SiteNameAvailableState = {
    readonly isAvailable: boolean;
    readonly isValidating: boolean;
    readonly siteName: string;
    readonly error: ErrorType;
};

const SITE_NAME_REGEX = /^[a-z0-9]+[a-z0-9-]+[a-z0-9]+$/;

export function useSiteNameValidation(
    siteName: string,
    onValidationChange: (isValid: boolean) => void = () => null
): SiteNameValidation {
    const [validation, setValidation] = useState<SiteNameValidationState>({
        isValid: undefined,
        error: ErrorType.NO_ERROR
    });
    const [availability, setAvailable] = useState<SiteNameAvailableState>({
        isValidating: false,
        isAvailable: false,
        error: ErrorType.NO_ERROR,
        siteName: ""
    });
    const [invalidTimes, setInvalidTimes] = useState<number>(0);

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const stargateClient = useBxpWebStargateClient();

    useEffect(() => {
        validateSiteNameDebounced.cancel();
        if (!validateSiteNameLength(siteName, setValidation)) {
            return;
        }
        ufoEventSiteNameAvailability.setCustomData({
            invalidTimes
        });
        validateSiteNameDebounced(
            siteName,
            stargateClient,
            setAvailable,
            createAnalyticsEvent
        )?.catch(() => {
            setAvailable({
                siteName,
                isAvailable: false,
                isValidating: false,
                error: ErrorType.NO_ERROR
            });
        });
    }, [siteName]);

    useEffect(() => {
        onValidationChange(
            !!validation.isValid &&
                validation.error === ErrorType.NO_ERROR &&
                availability.isAvailable &&
                siteName === availability.siteName &&
                availability.error === ErrorType.NO_ERROR
        );
        if (validation.error === ErrorType.INVALID_NAME) {
            setInvalidTimes(invalidTimes + 1);
        }
    }, [validation, availability]);

    return {
        isValidating: availability.isValidating,
        isValid:
            validation.isValid &&
            availability.isAvailable &&
            siteName === availability.siteName,
        error:
            validation.error === ErrorType.NO_ERROR
                ? availability.error
                : validation.error
    };
}

const validateSiteNameLength = (
    siteName: string,
    setValidation: Dispatch<SetStateAction<SiteNameValidationState>>
) => {
    if (siteName.length === 0) {
        setValidation({
            isValid: false,
            error: ErrorType.NO_ERROR
        });
        return false;
    } else if (siteName.length < 3) {
        setValidation({
            isValid: false,
            error: ErrorType.INVALID_NAME
        });
        return false;
    } else if (siteName.length > 50) {
        setValidation({
            isValid: false,
            error: ErrorType.MAXIMUM_LENGTH
        });
        return false;
    } else if (!SITE_NAME_REGEX.test(siteName)) {
        setValidation({
            isValid: false,
            error: ErrorType.INVALID_NAME
        });
        return false;
    }

    setValidation({
        isValid: true,
        error: ErrorType.NO_ERROR
    });
    return true;
};

const validateSiteNameDebounced = debounce(validateSiteName, 300, {
    trailing: true
});

async function validateSiteName(
    siteName: string,
    stargateClient: IBxpWebStargateClient,
    setAvailable: Dispatch<SetStateAction<SiteNameAvailableState>>,
    createAnalyticsEvent: CreateUIAnalyticsEvent
): Promise<void> {
    try {
        ufoEventSiteNameAvailability.start({ webPlatform: "bxp-ui" });
        const { availability } =
            await stargateClient.checkCloudNameAvailability({
                cloudName: siteName
            });

        ufoEventSiteNameAvailability.success({
            customData: {
                siteName,
                availability
            }
        });
        createAnalyticsEvent({
            isNewRelic: true,
            isAjax: true,
            apiName: "react-signup-site-validation",
            action: "react-signup-site-validation",
            success: true,
            response: { status: "200", apiUrl: "site-availability-url" }
        }).fire("signup");

        switch (availability) {
            case "AVAILABLE":
                return setAvailable({
                    siteName,
                    isValidating: false,
                    isAvailable: true,
                    error: ErrorType.NO_ERROR
                });
            default:
                return setAvailable({
                    siteName,
                    isValidating: false,
                    isAvailable: false,
                    error: ErrorType.UNAVAILABLE
                });
        }
    } catch (e) {
        createAnalyticsEvent({
            isNewRelic: true,
            isAjax: true,
            apiName: "react-signup-site-validation",
            action: "react-signup-site-validation",
            success: false,
            response: undefined,
            additionalInfo: e
        }).fire("signup");
        return setAvailable({
            siteName,
            isValidating: false,
            isAvailable: false,
            error: ErrorType.NETWORK_ERROR
        });
    }
}
